import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import { TEXT_DIRECTION } from 'helpers/isRtlLanguage';
import PropTypes from 'prop-types';
import { CDN_TYPE_CMS, CDN_TYPE_GAME, imageUrlCDN, LocalConfig, normalizePath } from 'utils';

import { Slider } from '@atlas/components';
import {
  Bus,
  constants,
  CryptoFormatter,
  CurrencyFormatter,
  isUserActionNeedPhotoVerification,
  needsVerification,
  NotificationManager,
  PhotoVerificationAction,
  SiteConfigManager,
  useApplicationState,
  useCasinoLastWinners,
  useSettings,
} from '@atlas/core';
import { casinoRoutes } from '@atlas/routing';
import { CasinoLastWinnersTile, Label, Loader } from '@atlas/ui';

import { BASE_PATH } from 'common/constants';
import { useIsRtl } from 'hooks/useIsRtl';
import { useSelfRegulationContext } from 'src/components/SelfRegulationProvider';
import { useAccountBlocked } from 'src/hooks/useAccountBlocked';
import gamePlaceholder from 'static/assets/svg/icon-casino.svg';
import avatarPlaceholder from 'static/assets/svg/icon-user-avatar.svg';

const { game: casinoGamePath } = casinoRoutes;
const { CRYPTO_CURRENCY_CODE } = constants;

const QUEUE_LIMIT = 50;

const userName = login => {
  if (login.length > 3) return `${login.slice(0, 3)}*`;

  return `${login.slice(0, -2)}***`;
};

const callUserSelfExcludedPopup = () =>
  NotificationManager.notify({
    message: 'casino_error_client_self_excluded',
    title: 'error',
    type: 'notification',
  });

const formatWinnerItem = (winner, languageCode) => {
  const info = winner.info || {};
  const { iconUrl, id: gameId, name: gameName } = winner.game || {};

  const currency = info.currency ?? '';

  const formatter = CRYPTO_CURRENCY_CODE.includes(currency)
    ? new CryptoFormatter(languageCode, currency)
    : new CurrencyFormatter(languageCode, currency);

  const formattedAmount = formatter.format(info.amount ?? 0);

  const gameImage = iconUrl ? imageUrlCDN(iconUrl, CDN_TYPE_GAME) : gamePlaceholder;

  return {
    amount: formattedAmount,
    avatarUrl: avatarPlaceholder,
    currency: '',
    id: gameId,
    imageUrlCDN: gameImage,
    login: userName(info.login ?? ''),
    name: gameName ?? '',
    uniqId: info.transactionId,
  };
};

const convertToMilliseconds = seconds => seconds * 1000;
const DEFAULT_LAST_WINNER_SPEED = 2;

const LobbyLastWinnersBlock = ({ blockTitleKey, imageUrl }) => {
  const [isShowImg, setIsShowImg] = useState(true);
  const handleError = () => setIsShowImg(false);

  const projectId = LocalConfig.get('projectId');
  const { lastWinners = [], loading } = useCasinoLastWinners(projectId);

  const [winnersToShow, setWinnersToShow] = useState([]);

  const { lang } = useSettings();
  const applicationState = useApplicationState();
  const isAccountBlocked = useAccountBlocked();
  const { showSelfRegulationPopupIfNeeded, showSpentLimitPopupIfNeeded } =
    useSelfRegulationContext();
  const {
    account,
    account: { selfExcludedDueDate } = {},
    authenticated,
    bankAccounts,
  } = applicationState;

  const history = useHistory();
  const {
    location: { pathname, search },
    push: historyPush,
  } = history;

  const lastWinnersAutoplaySpeed = SiteConfigManager.getConfig(
    'config.config.lastWinners.mobile.update_time',
  );

  const sliderSpeed = useMemo(
    () =>
      lastWinnersAutoplaySpeed
        ? convertToMilliseconds(lastWinnersAutoplaySpeed)
        : convertToMilliseconds(DEFAULT_LAST_WINNER_SPEED),
    [lastWinnersAutoplaySpeed],
  );

  const isRtl = useIsRtl();
  const dir = useMemo(() => (isRtl ? TEXT_DIRECTION.RTL : TEXT_DIRECTION.LTR), [isRtl]);

  useEffect(() => {
    const handler = ({ data }) => {
      setWinnersToShow(prev => {
        const nextList = [...prev, data];
        if (nextList.length > QUEUE_LIMIT) {
          return nextList.slice(nextList.length - QUEUE_LIMIT);
        }
        return nextList;
      });
    };

    Bus.on(Bus.events.casino.lastWinnerUpdate, handler);

    return () => Bus.off(Bus.events.casino.lastWinnerUpdate, handler);
  }, []);

  useEffect(() => {
    const winnersList = lastWinners || [];
    if (winnersList.length === 0) return;

    setWinnersToShow(winnersList);
  }, [lastWinners]);

  const showAccountBlockedPopup = useCallback(() => {
    NotificationManager.display({
      id: 'accountBlocked',
      type: 'accountBlocked',
    });
  }, []);

  const path = normalizePath(pathname);

  const onPlayClick = useCallback(
    async gameId => {
      if (isAccountBlocked) {
        showAccountBlockedPopup();
        return;
      }

      if (selfExcludedDueDate && dayjs().isBefore(selfExcludedDueDate)) {
        return callUserSelfExcludedPopup();
      }
      const gameUrl = new URL(`${path}/${casinoGamePath}/${gameId}`, location.origin);

      gameUrl.searchParams.append('lastWinner', 'true');
      gameUrl.searchParams.set('from', pathname + search);

      if (
        authenticated &&
        needsVerification(account, bankAccounts) &&
        isUserActionNeedPhotoVerification(account, PhotoVerificationAction.CASINO_GAME)
      ) {
        return NotificationManager.display({
          data: { history },
          message: 'verificationPopup',
          type: 'verificationPopup',
        });
      }

      const canProceedSpentLimit = await showSpentLimitPopupIfNeeded();
      if (!canProceedSpentLimit) return;

      const canProceedSelfRegulation = await showSelfRegulationPopupIfNeeded();
      if (!canProceedSelfRegulation) return;

      return historyPush(gameUrl.pathname + gameUrl.search);
    },
    [
      isAccountBlocked,
      selfExcludedDueDate,
      path,
      authenticated,
      account,
      bankAccounts,
      showSpentLimitPopupIfNeeded,
      showSelfRegulationPopupIfNeeded,
      historyPush,
      showAccountBlockedPopup,
      history,
      pathname,
      search,
    ],
  );

  const languageCode = useMemo(() => {
    const currentLang = SiteConfigManager.getConfig('languages')?.find(
      language => language.code === lang,
    );
    return currentLang?.hreflang || currentLang?.alpha3Code;
  }, [lang]);

  const finalItems = useMemo(() => {
    return (winnersToShow || []).map(winner => formatWinnerItem(winner, languageCode));
  }, [winnersToShow, languageCode]);

  if (loading) {
    return (
      <div className="casino-lobby__item lobby-last-winners">
        <Loader className="lobby-last-winners__loader" />
      </div>
    );
  }

  if (finalItems.length === 0) {
    return null;
  }

  return (
    <div className="casino-lobby__item lobby-last-winners">
      <div className="casino-lobby__content lobby-last-winners__wr">
        <div className="lobby-last-winners__left">
          <div className="lobby-last-winners__promo">
            {isShowImg && (
              <div className="lobby-last-winners__icon">
                <img
                  alt={blockTitleKey}
                  onError={handleError}
                  role="presentation"
                  src={
                    imageUrl
                      ? `${encodeURI(imageUrlCDN(imageUrl, CDN_TYPE_CMS))}`
                      : `${BASE_PATH || ''}/static/assets/images/last-winner.svg`
                  }
                  width={66}
                />
              </div>
            )}
            {blockTitleKey && (
              <div className="lobby-last-winners__title">
                <Label message={blockTitleKey} />
              </div>
            )}
          </div>
        </div>
        <div className="lobby-last-winners__slider">
          <Slider
            autoplay={{
              delay: convertToMilliseconds(DEFAULT_LAST_WINNER_SPEED),
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            dir={dir}
            direction="horizontal"
            loop={false}
            slideToClickedSlide={true}
            slidesPerView="auto"
            spaceBetween={8}
            speed={sliderSpeed}
          >
            {finalItems.map((item, index) => (
              <Slider.Slide key={`${item.uniqId || 'winner'}-${index}`}>
                <CasinoLastWinnersTile
                  index={index}
                  isLoading={false}
                  isMobile={true}
                  item={item}
                  onPlayClick={onPlayClick}
                />
              </Slider.Slide>
            ))}
          </Slider>
        </div>
      </div>
    </div>
  );
};

LobbyLastWinnersBlock.propTypes = {
  blockTitleKey: PropTypes.string,
  imageUrl: PropTypes.string,
};

export default LobbyLastWinnersBlock;
